/**
 * Axios Client Configuration
 *
 * Centralized HTTP client with interceptors for:
 * - Request/Response logging
 * - Error handling
 * - Authentication (Better Auth cookies)
 * - Retry logic
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// Create axios instance with base configuration
const getBaseURL = () => {
  const apiBaseUrl = import.meta.env['VITE_API_BASE_URL'] as string | undefined;

  // If empty, use empty string for relative URLs (Vite proxy)
  if (!apiBaseUrl || apiBaseUrl === '') {
    return '';
  }

  return apiBaseUrl;
};

export const apiClient = axios.create({
  baseURL: getBaseURL(),
  timeout: Number(import.meta.env['VITE_API_TIMEOUT']) || 30000,
  withCredentials: true, // CRITICAL: Better Auth cookies
  // headers: {
  //   'Content-Type': 'application/json',
  // },
});

// Request Interceptor: Logging
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (import.meta.env['VITE_ENABLE_API_LOGGING'] === 'true') {
      console.log(
        `[API Request] ${config.method?.toUpperCase()} ${config.url}`,
        config.data
      );
    }
    return config;
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response Interceptor: Logging & Error Handling
apiClient.interceptors.response.use(
  (response) => {
    if (import.meta.env['VITE_ENABLE_API_LOGGING'] === 'true') {
      console.log(
        `[API Response] ${response.status} ${response.config.url}`,
        response.data
      );
    }
    return response;
  },
  async (error: AxiosError) => {
    // Log error details
    console.error('[API Response Error]', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.message,
      data: error.response?.data,
    });

    // Handle 401 Unauthorized - Redirect to login
    // ONLY for requests to OUR API (not external services like GCS)
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const currentPath = window.location.pathname;

      // Check if this is a request to our API
      const isOurApiRequest =
        requestUrl.startsWith('/') || // Relative URL
        requestUrl.includes(import.meta.env['VITE_API_BASE_URL'] as string) || // Absolute URL to our API
        requestUrl.includes('linkeddog-api'); // Our domain

      // Only redirect if:
      // 1. It's a request to OUR API (not GCS or other external services)
      // 2. User is not already on auth pages
      if (
        isOurApiRequest &&
        !currentPath.startsWith('/auth/login') &&
        !currentPath.startsWith('/auth/signup')
      ) {
        console.warn('[API Client] 401 on our API - redirecting to login');
        window.location.href = '/auth/login';
      } else if (!isOurApiRequest) {
        console.log('[API Client] 401 on external service - not redirecting');
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
